import { createApi } from '@reduxjs/toolkit/query/react';

import { URL } from 'api/constants';
import { addParamsToUrl } from 'utils/query';
import { calculateOffset } from 'utils/helpers';

import { apiQuery } from '../../query';
import { eventApi, tagTypes as eventTagTypes } from '../event';
import { payoutApi, tagTypes as payoutTagTypes } from '../payout';
import { analyticsApi, tagTypes as analyticsTagTypes } from '../analytics';
import { accountingApi, tagTypes as accountingTagTypes } from '../accounting';

import {
  Refund,
  CreateRefund,
  RefundsRequest,
  EmployeeRefund,
  RefundListRequest,
  RefundListResponse,
} from './types';

const tagTypes = ['Refund'];

export const refundApi = createApi({
  tagTypes,
  baseQuery: apiQuery,
  reducerPath: 'refundApi',
  endpoints: (build) => ({
    details: build.query<Refund, string>({
      query: (id) => ({
        method: 'get',
        url: URL.CREATE_REFUND.replace(':id', id),
      }),
    }),

    html: build.query<string, string>({
      query: (id) => ({
        method: 'get',
        url: URL.GET_REFUND_RECEIPT_HTML.replace(':id', id),
      }),
    }),

    employee: build.query<EmployeeRefund, RefundsRequest>({
      query: ({ id, ...query }) => ({
        method: 'get',
        url: addParamsToUrl(
          URL.GET_EMPLOYEE_REFUNDS.replace(':id', id.toString()),
          query
        ),
      }),
    }),

    list: build.query<RefundListResponse, RefundListRequest>({
      providesTags: tagTypes,
      query: (params) => {
        const { page = 1, limit = 20, ...otherParams } = params || {};
        const offset = calculateOffset(page, limit);
        const searchParams = {
          limit,
          offset,
          ...otherParams,
        };
        return {
          method: 'get',
          url: addParamsToUrl(URL.GET_REFUNDS, searchParams),
        };
      },
    }),

    create: build.mutation<Refund, CreateRefund>({
      invalidatesTags: tagTypes,
      query: ({ id, ...data }) => ({
        data,
        method: 'post',
        url: URL.CREATE_REFUND.replace(':id', id.toString()),
      }),
      onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(eventApi.util.invalidateTags(eventTagTypes));
          dispatch(payoutApi.util.invalidateTags(payoutTagTypes));
          dispatch(analyticsApi.util.invalidateTags(analyticsTagTypes));
          dispatch(accountingApi.util.invalidateTags(accountingTagTypes));
        } catch (error) {
          console.error(error);
        }
      },
    }),
  }),
});

export const {
  useHtmlQuery,
  useListQuery,
  useEmployeeQuery,
  useCreateMutation,
  useLazyDetailsQuery,
} = refundApi;

export type { Refund, EmployeeRefund, RefundsRequest };
